import type { ReactNode } from 'react';

import { cx } from './cx';

/**
 * A surface with a rule around it and a 6px radius, clipping whatever is inside.
 *
 * `overflow-hidden` is what lets a table run to the card's edge without square corners
 * poking through the rounded ones, and what makes a scrolling body scroll inside the card
 * rather than the page.
 */
export function Card({
  tone = 'rule',
  className,
  children,
}: {
  /**
   * The colour of the card's own border.
   *
   * A prop rather than a class the caller adds, because two border-colour utilities on one
   * element are decided by Tailwind's ordering rather than by which was written last — and
   * `border-rule` happens to win over `border-brand`, so the card that is supposed to be
   * unmissable would quietly be the same as every other card.
   */
  tone?: 'rule' | 'brand';
  className?: string;
  children?: ReactNode;
}) {
  return (
    <section
      className={cx(
        'bg-surface border rounded-card overflow-hidden',
        tone === 'brand' ? 'border-brand' : 'border-rule',
        className,
      )}
    >
      {children}
    </section>
  );
}

/**
 * The header strip: a 13px/600 title, an optional count badge, and the actions on the right.
 * `actions` is a slot rather than a list of props, because every card's right-hand side is
 * different and a props-per-button API would be longer than the JSX it replaces.
 */
export function CardHeader({
  title,
  actions,
  className,
  children,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  className?: string;
  /** Anything that belongs beside the title rather than at the right-hand end. */
  children?: ReactNode;
}) {
  return (
    // Wraps rather than squeezes. On one row the actions do not shrink and the title may, so
    // a wide pill in a narrow column pushed the title's text out from under it and was drawn
    // over it; wrapping puts the actions on a second line before anything has to shrink.
    <header
      className={cx(
        'flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5 px-3.5 py-2.5 border-b border-rule',
        className,
      )}
    >
      <div className="flex items-center gap-2 text-[13px] font-semibold min-w-0">
        {title}
        {children}
      </div>
      {/* `ml-auto` changes nothing on one line, where justify-between already puts the
          actions at the end; when the header wraps, it keeps them at the right. */}
      {actions ? <div className="flex items-center gap-2 shrink-0 ml-auto">{actions}</div> : null}
    </header>
  );
}
